import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import '../styles/Admin.css';

// Define types for our data
interface Visitor {
  name: string;
  company: string;
  contact: string;
  email: string;
  contactPerson: string;
  isGroup: boolean;
  groupSize: number;
  date: string;
  time: string;
}

interface WeeklyData {
  week: string;
  visitors: number;
}

interface Stats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  groupVisits: number;
  individualVisits: number;
}

interface SortConfig {
  key: 'date' | 'name' | 'company' | 'time';
  direction: 'asc' | 'desc';
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [weeklyData, setWeeklyData] = useState<WeeklyData[]>([]);
  const [stats, setStats] = useState<Stats>({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    groupVisits: 0,
    individualVisits: 0
  });
  const [visitorData, setVisitorData] = useState<Visitor[]>([]);
  const [filteredData, setFilteredData] = useState<Visitor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'desc' });

  const handleBack = () => {
    navigate('/');
  };

  // Fetch visitor data for both dashboard and visitor logs
  useEffect(() => {
    const fetchVisitorData = async () => {
      try {
        const response = await axios.get('https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/visitors');
        const data = response.data;
        console.log("API Response:", data);
        
        // Format data for visitor logs
        const formattedData = data.visitors.map(visitor => ({
          name: visitor.visitorName,
          company: visitor.visitorCompany,
          contact: String(visitor.visitorContact),
          email: visitor.visitorEmail,
          contactPerson: visitor.contactPerson,
          isGroup: visitor.isGroup,
          groupSize: visitor.groupSize,
          date: visitor.visitDate,
          time: visitor.timeIn,
        }));
        setVisitorData(formattedData);
        setFilteredData(formattedData);
        
        // Calculate stats for dashboard
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        startOfWeek.setHours(0, 0, 0, 0);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        
        const currentStats = {
          today: data.visitors.filter(v => v.visitDate === today.toISOString().slice(0, 10)).length,
          thisWeek: data.visitors.filter(v => new Date(v.visitDate) >= startOfWeek).length,
          thisMonth: data.visitors.filter(v => new Date(v.visitDate) >= startOfMonth).length,
          groupVisits: data.visitors.filter(v => v.isGroup).length,
          individualVisits: data.visitors.filter(v => !v.isGroup).length
        };
        setStats(currentStats);

        // Calculate weekly trend
        const weeklyVisits = {};
        data.visitors.forEach(visitor => {
          const visitDate = new Date(visitor.visitDate);
          const weekStart = new Date(visitDate);
          weekStart.setDate(visitDate.getDate() - visitDate.getDay() + 1);
          const weekKey = weekStart.toISOString().slice(0, 10);
          weeklyVisits[weekKey] = (weeklyVisits[weekKey] || 0) + 1;
        });

        const sortedData = Object.entries(weeklyVisits)
          .map(([date, count]) => ({
            week: date,
            visitors: count as number
          }))
          .sort((a, b) => a.week.localeCompare(b.week));

        setWeeklyData(sortedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching visitor data:', err);
        setError('Failed to load visitor data.');
        setLoading(false);
      }
    };

    fetchVisitorData();
  }, []);

  // Handle sorting
  const handleSort = (key: SortConfig['key']) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Apply sorting and filtering
  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    let filtered = visitorData.filter(visitor =>
      (visitor.name?.toLowerCase().includes(lowerCaseQuery) || '') ||
      (visitor.company?.toLowerCase().includes(lowerCaseQuery) || '') ||
      (visitor.contact?.includes(searchQuery) || '') ||
      (visitor.email?.toLowerCase().includes(lowerCaseQuery) || '') ||
      (visitor.contactPerson?.toLowerCase().includes(lowerCaseQuery) || '')
    );
    
    if (startDate && endDate) {
      filtered = filtered.filter(visitor => {
        const visitorDate = new Date(visitor.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return visitorDate >= start && visitorDate <= end;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(a.date + 'T' + a.time);
        const dateB = new Date(b.date + 'T' + b.time);
        return sortConfig.direction === 'desc' ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
      }
      if (sortConfig.key === 'name' || sortConfig.key === 'company') {
        const valueA = (a[sortConfig.key] || '').toLowerCase();
        const valueB = (b[sortConfig.key] || '').toLowerCase();
        return sortConfig.direction === 'desc' ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      return 0;
    });

    // Always show only the top 10 latest visitors by default when no search/filters are active
    if (!searchQuery && !startDate && !endDate) {
      filtered = filtered.slice(0, 10);
    }

    setFilteredData(filtered);
  }, [searchQuery, startDate, endDate, visitorData, sortConfig]);

  const downloadCSV = () => {
    const csvContent = [
      ["Name", "Company", "Contact", "Email", "Red Rocket Contact", "Visit Type", "Group Size", "Date", "Time"], // Headers
      ...filteredData.map(visitor => [
        visitor.name,
        visitor.company,
        visitor.contact,
        visitor.email,
        visitor.contactPerson,
        visitor.isGroup ? 'Group Visit' : 'Individual Visit',
        visitor.isGroup ? visitor.groupSize : '1',
        visitor.date,
        visitor.time,
      ]),
    ]
      .map(row => row.join(",")) // Join each row with commas
      .join("\n"); // Join all rows with newlines

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `FilteredVisitorLogs_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // Render Dashboard component
  const renderDashboard = () => {
    // Calculate percentage changes
    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0) return 100;
      return ((current - previous) / previous) * 100;
    };

    // Get previous period stats
    const getPreviousStats = () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);

      return {
        yesterday: visitorData.filter(v => v.date === yesterday.toISOString().slice(0, 10)).length,
        lastWeek: visitorData.filter(v => {
          const visitDate = new Date(v.date);
          return visitDate >= lastWeek && visitDate < new Date(today.setDate(today.getDate() - 7));
        }).length,
        lastMonth: visitorData.filter(v => {
          const visitDate = new Date(v.date);
          return visitDate >= lastMonth && visitDate < new Date(today.getFullYear(), today.getMonth(), 1);
        }).length
      };
    };

    const previousStats = getPreviousStats();
    const todayChange = calculatePercentageChange(stats.today, previousStats.yesterday);
    const weekChange = calculatePercentageChange(stats.thisWeek, previousStats.lastWeek);
    const monthChange = calculatePercentageChange(stats.thisMonth, previousStats.lastMonth);

    return (
      <div className="dashboard">
        <h2>Visitor Dashboard</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Today's Visitors</h3>
              <p className="stat-value">{stats.today}</p>
              <p className={`stat-change ${todayChange >= 0 ? 'positive' : 'negative'}`}>
                {todayChange >= 0 ? '↑' : '↓'} {Math.abs(todayChange).toFixed(1)}% vs yesterday
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div className="stat-content">
              <h3>This Week</h3>
              <p className="stat-value">{stats.thisWeek}</p>
              <p className={`stat-change ${weekChange >= 0 ? 'positive' : 'negative'}`}>
                {weekChange >= 0 ? '↑' : '↓'} {Math.abs(weekChange).toFixed(1)}% vs last week
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <path d="M22 6l-10 7L2 6"></path>
              </svg>
            </div>
            <div className="stat-content">
              <h3>This Month</h3>
              <p className="stat-value">{stats.thisMonth}</p>
              <p className={`stat-change ${monthChange >= 0 ? 'positive' : 'negative'}`}>
                {monthChange >= 0 ? '↑' : '↓'} {Math.abs(monthChange).toFixed(1)}% vs last month
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Group Visits</h3>
              <p className="stat-value">{stats.groupVisits}</p>
              <p className="stat-subtitle">Total group visits</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Individual Visits</h3>
              <p className="stat-value">{stats.individualVisits}</p>
              <p className="stat-subtitle">Total individual visits</p>
            </div>
          </div>
        </div>
        <div className="chart-container">
          <h3>Weekly Visitor Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="week" 
                stroke="#ffffff"
                tick={{ fill: '#ffffff', angle: -45, textAnchor: 'end' }}
                height={60}
              />
              <YAxis 
                stroke="#ffffff"
                tick={{ fill: '#ffffff' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  color: '#ffffff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="visitors" 
                stroke="#e31837" 
                strokeWidth={2}
                dot={{ fill: '#e31837', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#ffffff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Render Visitor Logs component
  const renderVisitorLogs = () => {
    return (
      <div className="visitor-logs">
        <h2>Visitor Logs</h2>
        {loading && <p>Loading visitor logs...</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !error && (
          <>
            <div className="filters">
              <input
                type="text"
                placeholder="Search by name, company, contact, email, or Red Rocket contact"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <div className="date-range-inputs">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                />
                <span>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <button onClick={downloadCSV} className="export-button">
                Export Data
              </button>
            </div>
            <div className="table-header">
              <h3>{!searchQuery && !startDate && !endDate ? 'Top 10 Latest Visitors' : 'Filtered Visitor Logs'}</h3>
              <p>
                {!searchQuery && !startDate && !endDate 
                  ? 'Showing the most recent visitors by default. Use filters to see more.'
                  : 'Showing all matching results. Clear filters to see latest 10 visitors.'}
              </p>
            </div>
            <table className="visitor-table">
              <thead>
                <tr>
                  <th className="number-column">#</th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name {sortConfig.key === 'name' && <span>{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th onClick={() => handleSort('company')} className="sortable">
                    Company {sortConfig.key === 'company' && <span>{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th>Contact</th>
                  <th>Red Rocket Contact</th>
                  <th>Visit Type</th>
                  <th>Group Size</th>
                  <th onClick={() => handleSort('date')} className="sortable">
                    Date {sortConfig.key === 'date' && <span>{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>}
                  </th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((visitor, index) => (
                    <tr key={index}>
                      <td className="number-column">{index + 1}</td>
                      <td>{visitor.name}</td>
                      <td>{visitor.company}</td>
                      <td>{visitor.contact}</td>
                      <td>{visitor.contactPerson}</td>
                      <td>{visitor.isGroup ? 'Group Visit' : 'Individual Visit'}</td>
                      <td>{visitor.isGroup ? visitor.groupSize : '1'}</td>
                      <td>{visitor.date}</td>
                      <td>{visitor.time}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9}>No visitors found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="admin-container">
      <div className="video-overlay"></div>
      <video className="background-video" autoPlay loop muted playsInline>
        <source
          src="https://redrocket.energy/wp-content/uploads/2024/05/RedRocketAnimation1200p_Loop.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      <motion.button 
        className="back-button"
        onClick={handleBack}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M19 12H5M5 12L12 19M5 12L12 5" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
        Back to Home
      </motion.button>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="admin-content"
      >
        <div className="admin-header">
          <motion.img 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            src="https://redrocket.energy/wp-content/uploads/2021/03/rr-emblem.svg" 
            alt="Red Rocket Logo" 
            className="logo" 
          />
          <h1>Admin Dashboard</h1>
        </div>

        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`tab-button ${activeTab === 'visitor-logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('visitor-logs')}
          >
            Visitor Logs
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'dashboard' ? renderDashboard() : renderVisitorLogs()}
        </div>
      </motion.div>
    </div>
  );
};

export default Admin; 